import { SocialProfileNetworkType } from '@/types/socialProfileType';

import { FEATURES } from '@/lib/pricing/features';
import { FACEBOOK, INSTAGRAM, LINKEDIN, PINTEREST, TIKTOK_BUSINESS, X } from '@/lib/socialProfile';

type PlanMetaData = {
    key: string;
    maxProfiles: number;
    maxUsers: number;
    socialProfileTypes: SocialProfileNetworkType[];
    features: DeepValues<typeof FEATURES>[];
};

const EXCEL_PLAN: PlanMetaData = {
    key: 'excel',
    maxProfiles: 5,
    maxUsers: 6,
    socialProfileTypes: [INSTAGRAM, FACEBOOK, TIKTOK_BUSINESS, LINKEDIN, PINTEREST, X],
    features: [
        FEATURES.ANALYTICS.SOCIAL_PROFILE,
        FEATURES.ANALYTICS.GROUP,
        FEATURES.ANALYTICS.REELS,
        FEATURES.ANALYTICS.POSTS,
        FEATURES.ANALYTICS.TAGS_AND_MENTIONS,
        FEATURES.ANALYTICS.DASHBOARD,
        FEATURES.REPORTING.ACCOUNT_ANALYTICS,
        FEATURES.REPORTING.COMPETITORS,
        FEATURES.REPORTING.HASHTAGS,
        FEATURES.REPORTING.DASHBOARD,
        FEATURES.REPORTING.DASHBOARD_CUSTOMIZATION,
        FEATURES.CONTENT.POSTS,
        FEATURES.CONTENT.ALBUMS,
        FEATURES.CONTENT.CAMPAIGN_TRACKING,
        FEATURES.CONTENT.LABELS,
        FEATURES.PUBLISHING.POST_SCHEDULING,
        FEATURES.PUBLISHING.STORY_SCHEDULING,
        FEATURES.PUBLISHING.REELS_SCHEDULING,
        FEATURES.PUBLISHING.CROSS_POSTING,
        FEATURES.PUBLISHING.FIRST_COMMENT_SCHEDULING,
        FEATURES.PUBLISHING.MEDIA_LIBRARY,
        FEATURES.PUBLISHING.SAVED_CAPTIONS,
        FEATURES.PUBLISHING.POST_APPROVAL_AND_COLLABORATION,
        FEATURES.CONVERSATIONS.COMMENT_TRACKING,
        FEATURES.CONVERSATIONS.MENTIONS_TRACKING,
        FEATURES.CONVERSATIONS.DM_MANAGEMENT,
        FEATURES.LISTING.COMPETITORS,
        FEATURES.LISTING.HASHTAGS,
        FEATURES.LISTING.CUSTOMIZED_FEEDS,
        FEATURES.LISTING.INDUSTRY_BENCHMARK,
        FEATURES.TOOLS_AND_SERVICES.MOBILE_APP
    ]
} as const;

const AGENCY_PLAN: PlanMetaData = {
    key: 'agency',
    maxProfiles: -1, // unlimited
    maxUsers: -1, // unlimited
    socialProfileTypes: [INSTAGRAM, FACEBOOK, TIKTOK_BUSINESS, LINKEDIN, PINTEREST, X],
    features: [
        FEATURES.ANALYTICS.SOCIAL_PROFILE,
        FEATURES.ANALYTICS.GROUP,
        FEATURES.ANALYTICS.REELS,
        FEATURES.ANALYTICS.POSTS,
        FEATURES.ANALYTICS.TAGS_AND_MENTIONS,
        FEATURES.ANALYTICS.DASHBOARD,
        FEATURES.REPORTING.ACCOUNT_ANALYTICS,
        FEATURES.REPORTING.COMPETITORS,
        FEATURES.REPORTING.HASHTAGS,
        FEATURES.REPORTING.DASHBOARD,
        FEATURES.REPORTING.DASHBOARD_CUSTOMIZATION,
        FEATURES.REPORTING.COMPANY_BRANDED_PDF,
        FEATURES.CONTENT.POSTS,
        FEATURES.CONTENT.ALBUMS,
        FEATURES.CONTENT.CAMPAIGN_TRACKING,
        FEATURES.CONTENT.LABELS,
        FEATURES.PUBLISHING.POST_SCHEDULING,
        FEATURES.PUBLISHING.STORY_SCHEDULING,
        FEATURES.PUBLISHING.REELS_SCHEDULING,
        FEATURES.PUBLISHING.CROSS_POSTING,
        FEATURES.PUBLISHING.FIRST_COMMENT_SCHEDULING,
        FEATURES.PUBLISHING.MEDIA_LIBRARY,
        FEATURES.PUBLISHING.SAVED_CAPTIONS,
        FEATURES.PUBLISHING.POST_APPROVAL_AND_COLLABORATION,
        FEATURES.CONVERSATIONS.COMMENT_TRACKING,
        FEATURES.CONVERSATIONS.MENTIONS_TRACKING,
        FEATURES.CONVERSATIONS.DM_MANAGEMENT,
        FEATURES.LISTING.COMPETITORS,
        FEATURES.LISTING.HASHTAGS,
        FEATURES.LISTING.CUSTOMIZED_FEEDS,
        FEATURES.LISTING.INDUSTRY_BENCHMARK,
        FEATURES.TOOLS_AND_SERVICES.MOBILE_APP
    ]
} as const;

const PRO_PLAN: PlanMetaData = {
    key: 'pro',
    maxProfiles: 3,
    maxUsers: 2,
    socialProfileTypes: [INSTAGRAM, FACEBOOK, TIKTOK_BUSINESS, LINKEDIN, PINTEREST],
    features: [
        FEATURES.ANALYTICS.SOCIAL_PROFILE,
        FEATURES.ANALYTICS.GROUP,
        FEATURES.ANALYTICS.REELS,
        FEATURES.ANALYTICS.POSTS,
        FEATURES.ANALYTICS.TAGS_AND_MENTIONS,
        FEATURES.ANALYTICS.DASHBOARD,
        FEATURES.REPORTING.ACCOUNT_ANALYTICS,
        FEATURES.REPORTING.COMPETITORS,
        FEATURES.REPORTING.DASHBOARD,
        FEATURES.REPORTING.DASHBOARD_CUSTOMIZATION,
        FEATURES.REPORTING.COMPANY_BRANDED_PDF,
        FEATURES.CONTENT.POSTS,
        FEATURES.CONTENT.ALBUMS,
        FEATURES.CONTENT.CAMPAIGN_TRACKING,
        FEATURES.CONTENT.LABELS,
        FEATURES.PUBLISHING.POST_SCHEDULING,
        FEATURES.PUBLISHING.STORY_SCHEDULING,
        FEATURES.PUBLISHING.REELS_SCHEDULING,
        FEATURES.PUBLISHING.CROSS_POSTING,
        FEATURES.PUBLISHING.FIRST_COMMENT_SCHEDULING,
        FEATURES.PUBLISHING.MEDIA_LIBRARY,
        FEATURES.PUBLISHING.SAVED_CAPTIONS,
        FEATURES.PUBLISHING.POST_APPROVAL_AND_COLLABORATION,
        FEATURES.CONVERSATIONS.COMMENT_TRACKING,
        FEATURES.CONVERSATIONS.MENTIONS_TRACKING,
        FEATURES.LISTING.COMPETITORS,
        FEATURES.LISTING.CUSTOMIZED_FEEDS,
        FEATURES.LISTING.INDUSTRY_BENCHMARK,
        FEATURES.TOOLS_AND_SERVICES.MOBILE_APP
    ]
} as const;

const ADVANCED_PLAN: PlanMetaData = {
    key: 'advanced',
    maxProfiles: 5,
    maxUsers: -1, // unlimited
    socialProfileTypes: [INSTAGRAM, FACEBOOK, TIKTOK_BUSINESS, LINKEDIN, PINTEREST],
    features: [
        FEATURES.ANALYTICS.SOCIAL_PROFILE,
        FEATURES.ANALYTICS.GROUP,
        FEATURES.ANALYTICS.REELS,
        FEATURES.ANALYTICS.POSTS,
        FEATURES.ANALYTICS.TAGS_AND_MENTIONS,
        FEATURES.ANALYTICS.DASHBOARD,
        FEATURES.REPORTING.ACCOUNT_ANALYTICS,
        FEATURES.REPORTING.COMPETITORS,
        FEATURES.REPORTING.HASHTAGS,
        FEATURES.REPORTING.DASHBOARD,
        FEATURES.REPORTING.DASHBOARD_CUSTOMIZATION,
        FEATURES.REPORTING.COMPANY_BRANDED_PDF,
        FEATURES.CONTENT.POSTS,
        FEATURES.CONTENT.ALBUMS,
        FEATURES.CONTENT.CAMPAIGN_TRACKING,
        FEATURES.CONTENT.LABELS,
        FEATURES.PUBLISHING.POST_SCHEDULING,
        FEATURES.PUBLISHING.STORY_SCHEDULING,
        FEATURES.PUBLISHING.REELS_SCHEDULING,
        FEATURES.PUBLISHING.CROSS_POSTING,
        FEATURES.PUBLISHING.FIRST_COMMENT_SCHEDULING,
        FEATURES.PUBLISHING.MEDIA_LIBRARY,
        FEATURES.PUBLISHING.SAVED_CAPTIONS,
        FEATURES.PUBLISHING.POST_APPROVAL_AND_COLLABORATION,
        FEATURES.CONVERSATIONS.COMMENT_TRACKING,
        FEATURES.CONVERSATIONS.MENTIONS_TRACKING,
        FEATURES.LISTING.COMPETITORS,
        FEATURES.LISTING.HASHTAGS,
        FEATURES.LISTING.CUSTOMIZED_FEEDS,
        FEATURES.LISTING.INDUSTRY_BENCHMARK,
        FEATURES.TOOLS_AND_SERVICES.MOBILE_APP
    ]
} as const;

export const PLANS: Record<string, PlanMetaData> = {
    trial: {
        ...EXCEL_PLAN,
        key: 'trial',
        socialProfileTypes: [INSTAGRAM, FACEBOOK, TIKTOK_BUSINESS, LINKEDIN, PINTEREST]
    },
    single: {
        key: 'single',
        maxProfiles: 5,
        maxUsers: 1,
        socialProfileTypes: [INSTAGRAM, FACEBOOK, TIKTOK_BUSINESS, LINKEDIN, PINTEREST],
        features: [
            FEATURES.ANALYTICS.SOCIAL_PROFILE,
            FEATURES.ANALYTICS.GROUP,
            FEATURES.ANALYTICS.REELS,
            FEATURES.ANALYTICS.POSTS,
            FEATURES.ANALYTICS.TAGS_AND_MENTIONS,
            FEATURES.ANALYTICS.DASHBOARD,
            FEATURES.REPORTING.ACCOUNT_ANALYTICS,
            FEATURES.REPORTING.COMPETITORS,
            FEATURES.REPORTING.HASHTAGS,
            FEATURES.REPORTING.DASHBOARD,
            FEATURES.REPORTING.DASHBOARD_CUSTOMIZATION,
            FEATURES.REPORTING.COMPANY_BRANDED_PDF,
            FEATURES.CONTENT.POSTS,
            FEATURES.CONTENT.ALBUMS,
            FEATURES.CONTENT.CAMPAIGN_TRACKING,
            FEATURES.CONTENT.LABELS,
            FEATURES.PUBLISHING.POST_SCHEDULING,
            FEATURES.PUBLISHING.STORY_SCHEDULING,
            FEATURES.PUBLISHING.REELS_SCHEDULING,
            FEATURES.PUBLISHING.CROSS_POSTING,
            FEATURES.PUBLISHING.FIRST_COMMENT_SCHEDULING,
            FEATURES.PUBLISHING.MEDIA_LIBRARY,
            FEATURES.PUBLISHING.SAVED_CAPTIONS,
            FEATURES.PUBLISHING.POST_APPROVAL_AND_COLLABORATION,
            FEATURES.CONVERSATIONS.COMMENT_TRACKING,
            FEATURES.CONVERSATIONS.MENTIONS_TRACKING,
            FEATURES.LISTING.COMPETITORS,
            FEATURES.LISTING.HASHTAGS,
            FEATURES.LISTING.CUSTOMIZED_FEEDS,
            FEATURES.LISTING.INDUSTRY_BENCHMARK,
            FEATURES.TOOLS_AND_SERVICES.MOBILE_APP
        ]
    },
    launch: {
        key: 'launch',
        maxProfiles: 5,
        maxUsers: 1,
        socialProfileTypes: [INSTAGRAM, FACEBOOK, TIKTOK_BUSINESS, LINKEDIN, PINTEREST],
        features: [
            FEATURES.ANALYTICS.SOCIAL_PROFILE,
            FEATURES.ANALYTICS.GROUP,
            FEATURES.ANALYTICS.REELS,
            FEATURES.REPORTING.ACCOUNT_ANALYTICS,
            FEATURES.CONTENT.POSTS,
            FEATURES.PUBLISHING.POST_SCHEDULING,
            FEATURES.CONVERSATIONS.COMMENT_TRACKING,
            FEATURES.LISTING.COMPETITORS,
            FEATURES.TOOLS_AND_SERVICES.MOBILE_APP
        ]
    },
    scale: {
        key: 'scale',
        maxProfiles: 5,
        maxUsers: 3,
        socialProfileTypes: [INSTAGRAM, FACEBOOK, TIKTOK_BUSINESS, LINKEDIN, PINTEREST],
        features: [
            FEATURES.ANALYTICS.SOCIAL_PROFILE,
            FEATURES.ANALYTICS.GROUP,
            FEATURES.ANALYTICS.REELS,
            FEATURES.ANALYTICS.POSTS,
            FEATURES.ANALYTICS.TAGS_AND_MENTIONS,
            FEATURES.REPORTING.ACCOUNT_ANALYTICS,
            FEATURES.REPORTING.COMPETITORS,
            FEATURES.REPORTING.HASHTAGS,
            FEATURES.CONTENT.POSTS,
            FEATURES.CONTENT.ALBUMS,
            FEATURES.CONTENT.LABELS,
            FEATURES.CONTENT.CAMPAIGN_TRACKING,
            FEATURES.PUBLISHING.POST_SCHEDULING,
            FEATURES.PUBLISHING.STORY_SCHEDULING,
            FEATURES.PUBLISHING.REELS_SCHEDULING,
            FEATURES.PUBLISHING.CROSS_POSTING,
            FEATURES.PUBLISHING.FIRST_COMMENT_SCHEDULING,
            FEATURES.PUBLISHING.MEDIA_LIBRARY,
            FEATURES.PUBLISHING.SAVED_CAPTIONS,
            FEATURES.CONVERSATIONS.COMMENT_TRACKING,
            FEATURES.CONVERSATIONS.MENTIONS_TRACKING,
            FEATURES.CONVERSATIONS.DM_MANAGEMENT,
            FEATURES.LISTING.COMPETITORS,
            FEATURES.LISTING.HASHTAGS,
            FEATURES.LISTING.CUSTOMIZED_FEEDS,
            FEATURES.TOOLS_AND_SERVICES.MOBILE_APP
        ]
    },
    excel: EXCEL_PLAN,
    custom: AGENCY_PLAN,
    agency: AGENCY_PLAN,
    teams: {
        key: 'teams',
        maxProfiles: 10,
        maxUsers: 10,
        socialProfileTypes: [INSTAGRAM, FACEBOOK, TIKTOK_BUSINESS, LINKEDIN, PINTEREST],
        features: [
            FEATURES.ANALYTICS.SOCIAL_PROFILE,
            FEATURES.ANALYTICS.GROUP,
            FEATURES.ANALYTICS.REELS,
            FEATURES.ANALYTICS.POSTS,
            FEATURES.ANALYTICS.TAGS_AND_MENTIONS,
            FEATURES.ANALYTICS.DASHBOARD,
            FEATURES.REPORTING.ACCOUNT_ANALYTICS,
            FEATURES.REPORTING.COMPETITORS,
            FEATURES.REPORTING.HASHTAGS,
            FEATURES.REPORTING.DASHBOARD,
            FEATURES.REPORTING.DASHBOARD_CUSTOMIZATION,
            FEATURES.REPORTING.COMPANY_BRANDED_PDF,
            FEATURES.CONTENT.POSTS,
            FEATURES.CONTENT.ALBUMS,
            FEATURES.CONTENT.CAMPAIGN_TRACKING,
            FEATURES.CONTENT.LABELS,
            FEATURES.PUBLISHING.POST_SCHEDULING,
            FEATURES.PUBLISHING.STORY_SCHEDULING,
            FEATURES.PUBLISHING.REELS_SCHEDULING,
            FEATURES.PUBLISHING.CROSS_POSTING,
            FEATURES.PUBLISHING.FIRST_COMMENT_SCHEDULING,
            FEATURES.PUBLISHING.MEDIA_LIBRARY,
            FEATURES.PUBLISHING.SAVED_CAPTIONS,
            FEATURES.PUBLISHING.POST_APPROVAL_AND_COLLABORATION,
            FEATURES.CONVERSATIONS.COMMENT_TRACKING,
            FEATURES.CONVERSATIONS.MENTIONS_TRACKING,
            FEATURES.LISTING.COMPETITORS,
            FEATURES.LISTING.HASHTAGS,
            FEATURES.LISTING.CUSTOMIZED_FEEDS,
            FEATURES.LISTING.INDUSTRY_BENCHMARK,
            FEATURES.TOOLS_AND_SERVICES.MOBILE_APP
        ]
    },
    pro: PRO_PLAN,
    old_pro: {
        ...PRO_PLAN,
        key: 'old_pro'
    },
    advanced: ADVANCED_PLAN,
    old_advanced: {
        ...ADVANCED_PLAN,
        key: 'old_advanced'
    },
    enterprise: {
        key: 'enterprise',
        maxProfiles: -1, // unlimited
        maxUsers: -1, // unlimited
        socialProfileTypes: [INSTAGRAM, FACEBOOK, TIKTOK_BUSINESS, LINKEDIN, PINTEREST],
        features: [
            FEATURES.ANALYTICS.SOCIAL_PROFILE,
            FEATURES.ANALYTICS.GROUP,
            FEATURES.ANALYTICS.REELS,
            FEATURES.ANALYTICS.POSTS,
            FEATURES.ANALYTICS.TAGS_AND_MENTIONS,
            FEATURES.ANALYTICS.DASHBOARD,
            FEATURES.REPORTING.ACCOUNT_ANALYTICS,
            FEATURES.REPORTING.COMPETITORS,
            FEATURES.REPORTING.HASHTAGS,
            FEATURES.REPORTING.DASHBOARD,
            FEATURES.REPORTING.DASHBOARD_CUSTOMIZATION,
            FEATURES.REPORTING.COMPANY_BRANDED_PDF,
            FEATURES.CONTENT.POSTS,
            FEATURES.CONTENT.ALBUMS,
            FEATURES.CONTENT.CAMPAIGN_TRACKING,
            FEATURES.CONTENT.LABELS,
            FEATURES.PUBLISHING.POST_SCHEDULING,
            FEATURES.PUBLISHING.STORY_SCHEDULING,
            FEATURES.PUBLISHING.REELS_SCHEDULING,
            FEATURES.PUBLISHING.CROSS_POSTING,
            FEATURES.PUBLISHING.FIRST_COMMENT_SCHEDULING,
            FEATURES.PUBLISHING.MEDIA_LIBRARY,
            FEATURES.PUBLISHING.SAVED_CAPTIONS,
            FEATURES.PUBLISHING.POST_APPROVAL_AND_COLLABORATION,
            FEATURES.CONVERSATIONS.COMMENT_TRACKING,
            FEATURES.CONVERSATIONS.MENTIONS_TRACKING,
            FEATURES.LISTING.COMPETITORS,
            FEATURES.LISTING.HASHTAGS,
            FEATURES.LISTING.CUSTOMIZED_FEEDS,
            FEATURES.LISTING.INDUSTRY_BENCHMARK,
            FEATURES.TOOLS_AND_SERVICES.MOBILE_APP
        ]
    }
} as const;
